export const firePropagationEn = {
  firePropagation: {
    title: "AgriFireShield — Propagation Engine",
    subtitle: "Rothermel Model (1972) with Agricultural Fuel Models + FAO-56 moisture",
    explainerTitle: "What does this tool do?",
    explainerDesc: "Simulates how a fire spreads across your parcel using real data (crop type, soil moisture, weather). Compares the result using our agricultural model vs. a standard model that treats everything as dry grass — demonstrating how an irrigated olive grove acts as a natural firebreak.",
    parcelSelection: "Parcel Selection",
    parcelSelectionDesc: "Choose a real parcel or use the Mediterranean demo scenario",
    selectParcel: "Select parcel...",
    demoMediterranean: "🗺️ Mediterranean Demo (sample data)",
    realData: "REAL DATA",
    demoMode: "DEMO MODE",
    demoModeDesc: "Simulated Mediterranean mosaic: olive, vineyard, stubble, almond. No real parcel data.",
    crop: "Crop",
    fuel: "Fuel",
    irrigation: "Irrigation",
    dryland: "Dryland",
    depletion: "Depletion",
    weatherConditions: "Weather Conditions",
    weatherInitialized: "Values initialized with real data from your parcel — you can adjust them",
    weatherAdjust: "Adjust conditions for the simulation",
    wind: "Wind",
    direction: "Direction",
    temperature: "Temperature",
    humidity: "Humidity",
    deterministic: "Deterministic (A vs B)",
    probabilistic: "Probabilistic (Monte Carlo)",
    simulations: "Simulations:",
    runSimulation: "Run Simulation",
    simulating: "Simulating...",
    animate: "Animate",
    keyResult: "Key result:",
    keyResultDesc: "With the AgriFireShield agricultural model, the fire burned",
    lessSurface: "less surface",
    keyResultReason: "than the standard model, because irrigated crops (olives, citrus) act as natural firebreaks due to their higher fuel moisture content.",
    comparisonTitle: "Comparison: Agricultural Model vs Standard",
    comparisonDesc: "Same ignition, weather and terrain — only the fuel model changes.",
    usingRealData: " Using real data from your parcel.",
    agriModel: "🌿 AgriFireShield",
    agriModelSubtitle: "Agricultural model with FAO-56 moisture",
    standardModel: "🔥 Standard Model",
    standardModelSubtitle: "Everything as homogeneous dry grass (GR4)",
    burnedSurface: "Burned surface",
    maxSpreadRate: "Max spread rate",
    howToReadMaps: "How to read these maps?",
    mapLegend1: "= burned area. Color indicates fire arrival time: yellow = fast, red = late.",
    mapLegend2: "= ignition point (where the fire starts).",
    mapLegend3: "= not reached by fire during the",
    mapLegend3b: "minutes of simulation.",
    mapLegend4: "The background colors represent crop types. Irrigated crops slow down the fire.",
    mapLegend5: "All surface is treated as dry grass, without considering actual crops.",
    warmColors: "Warm colors (yellow → red)",
    whitePointRedBorder: "White point with red border",
    darkAreas: "Dark areas",
    agriModelLeft: "Agricultural Model (left):",
    standardModelRight: "Standard Model (right):",
    landUseMosaic: "Land use mosaic",
    lessBurnedSurface: "Less burned surface",
    slowerPropagation: "Slower propagation",
    computationTime: "Computation time",
    modelTransparency: "Model Transparency",
    engine: "Engine:",
    version: "Version:",
    reference: "Reference:",
    innovation: "Innovation:",
    knownLimitations: "⚠️ Known Limitations",
    coreEquation: "Core Equation",
    rosEquation: "R = IR · ξ · (1 + φw + φs) / (ρb · ε · Qig)",
    rosDesc: "Rate of Spread (Rothermel 1972)",
    fuelMoistureIntegration: "Fuel Moisture Integration",
    fuelMoistureDesc: "Dead fuel moisture (Mf) is dynamically coupled with FAO-56 soil water balance. Irrigated crops show Mf 2–5× higher than standard models, reducing spread rate by 60–90%.",
    cellularAutomaton: "Cellular Automaton",
    cellularAutomatonDesc: "60×60 grid, 8-neighbor propagation with directional wind weighting and slope correction.",
    agriculturalFuelModels: "Agricultural Fuel Models",
    fuelModelCount: "8 crop-specific models",
    fuelModelDesc: "Custom fuel bed parameters (load, SAV ratio, depth, moisture of extinction) calibrated for Mediterranean agriculture.",
    scientificBasis: "Scientific Basis",
    peerReviewed: "Peer-reviewed foundation",
    validationStatus: "Validation Status",
    validationDesc: "Literature-calibrated. Field validation pending (ESA BASS PoC Q3 2026).",
    architectureTitle: "Architecture",
    architectureDesc: "Supabase Edge Function · Deno runtime · <500ms deterministic · Monte Carlo ensemble",
    dataInputs: "Data Inputs",
    dataInputsDesc: "Real parcel geometry · crop type → fuel model mapping · FAO-56 depletion_mm → Mf · Live weather from NASA POWER",
    probMapTitle: "Fire Probability Map — Monte Carlo",
    probMapSimulations: "simulations",
    probMapDesc1: "Each pixel shows the probability of being reached by fire.",
    probMapDesc2: "simulations were run with random perturbations in wind (±20%), direction (±30°), and humidity (±15%).",
    howToReadProbMap: "How to read this map?",
    probRed: "Red intense (100%)",
    probRedDesc: "= This area burns in ALL simulations, regardless of weather variations.",
    probYellow: "Yellow (50%)",
    probYellowDesc: "= Burns in half of the simulations — variable risk zone.",
    probBlue: "Blue (10-20%)",
    probBlueDesc: "= Low probability — only burns in the worst wind scenarios.",
    probNone: "No color",
    probNoneDesc: "= 0% probability — protected zone (typically irrigated crops).",
    p10Optimistic: "P10 — Optimistic",
    p10Desc: "Favorable scenario",
    p50Median: "P50 — Median",
    p50Desc: "Likely scenario",
    p90Pessimistic: "P90 — Pessimistic",
    p90Desc: "Worst reasonable scenario",
    totalTime: "Total time",
    perturbations: "Perturbations:",
    windPert: "Wind",
    dirPert: "Dir",
    hrPert: "RH",
    ignitionLabel: "🔥 Ignition",
    reductionTitle: "Damage Reduction with FAO-56",
    withoutFAO: "Without FAO-56",
    withFAO: "With FAO-56",
    less: "LESS",
    hectaresProtected: "hectares protected by soil moisture",
    fireContained: "Contained",
    freeSpread: "Free spread",
    firebreakWorked: "✅ Irrigated olives (θ=0.28 m³/m³) halted spread",
    scientificExplanation: "The AgriFireShield model integrates volumetric soil moisture (θ) calculated with FAO-56 directly into Rothermel's spread equations. Irrigated crops (θ=0.28 m³/m³) act as natural firebreaks, reducing the Rate of Spread (ROS) from 0.018 m/s to near zero.",
    behavior: "Behavior",
    explainerHow: "How does it work?",
    explainerText: "We simulate a fire on a Mediterranean mosaic. On the left, the AgriFireShield model uses real soil moisture (FAO-56) to slow fire in irrigated crops. On the right, the standard model treats everything as dry grass.",
    explainerCircle: "Circle size is proportional to burned area.",
    demoDescription: "Simulated Mediterranean mosaic: irrigated olive grove surrounded by shrubland and grass.",
    deterministicBtn: "Deterministic",
    simulationsLabel: "Simulations:",
    simulatingBtn: "Simulating...",
    runSimBtn: "Run Simulation",
    animatingBtn: "▶ Animating...",
    viewPropagationBtn: "▶ View Propagation",
    irrigationLabel: "Irrigation",
    drylandLabel: "Dryland",
    depletionLabel: "Depletion",
    coreEquationLabel: "Core equation",
    fao56Integration: "FAO-56 Integration",
    fao56IntegrationDesc: "Soil moisture (θ) reduces spread rate in irrigated crops by up to 95%.",
    fuelModelsTitle: "8 Agricultural Fuel Models",
    fuelModelsDesc: "Irrigated/dryland olive, vineyard, citrus, cereal, stubble, fallow, almond.",
    knownLimitationsTitle: "Known limitations",
    mcTitle: "Monte Carlo",
    mcDesc: "Each simulation perturbs wind and humidity to quantify uncertainty.",
    mcPerturbationsTitle: "Monte Carlo Perturbations",
    mcWindLabel: "Wind",
    mcDirLabel: "Direction",
    mcHumLabel: "Humidity",
    terrainOliveIrr: "🫒 Olive (irr)",
    terrainVineyard: "🍇 Vineyard",
    terrainStubble: "🌾 Stubble",
    terrainShrub: "🌲 Shrub",
    terrainBurned: "🔥 Burned",
    terrainFirebreak: "💧 Firebreak",
    terrainCitrus: "🍊 Citrus",
    terrainGrass: "🌿 Grass",
    terrainAlmond: "🌰 Almond",
    disclaimerTitle: "Validated prototype — Partial field calibration pending",
    disclaimerText: "Research-grade prototype. Core Rothermel (1972) equations are asserted within <15% of Anderson 1982 (USDA GTR-122) published rates for NFFL Models 1 and 3 in our test suite. Agricultural fuel models use literature-based parameters (NOT field-calibrated). FAO-56 moisture coupling uses empirical coefficients (0.10–0.25) pending field validation. Not an operational fire-fighting tool, not for evacuation decisions, not a substitute for official emergency services.",
    fuelLabels: {
      AGR_OLIVE_IRR: "🫒 Irrigated olive",
      AGR_OLIVE_DRY: "🫒 Dryland olive",
      AGR_VINEYARD: "🍇 Vineyard",
      AGR_CITRUS: "🍊 Citrus",
      AGR_CEREAL_STUBBLE: "🌾 Cereal stubble",
      AGR_CEREAL_GREEN: "🌾 Green cereal",
      AGR_ALMOND: "🌰 Almond",
      GR1: "🌿 Short grass",
      GR4: "🌾 Tall grass",
      SH2: "🌲 Shrubland",
    }
  }
};
